import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { PendingFormContent } from "../../components/blocks/NativeFormResponseDialog/PendingFormContent";

interface NativeFormLinkContentProps {
  accessCode: string;
  onComplete: () => void;
  automationStepId?: string;
  documentGroupBackUrl?: string;
  publicIdentifier?: string;
}

/**
 * Renders a `/link/form` deeplink with the native wizard. The link carries only a form id and access
 * code, so the response the wizard needs is resolved from the access code via `info_for_access_code`
 * — the endpoint that authoritatively maps one code to one response.
 */
export const NativeFormLinkContent = ({
  accessCode,
  onComplete,
  automationStepId,
  documentGroupBackUrl,
  publicIdentifier,
}: NativeFormLinkContentProps) => {
  const { t } = useTranslation(["forms", "common"]);

  const {
    data: accessCodeInfo,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "form_responses",
    method: "info_for_access_code",
    request: { accessCode },
    enabled: !!accessCode,
  });

  const formResponse = accessCodeInfo?.response;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading form...")}</p>
        </div>
      </div>
    );
  }

  if (error || !formResponse) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-destructive">
          <p className="text-sm font-medium mb-1">{t("Unable to load form")}</p>
          <p className="text-xs text-muted-foreground">
            {error instanceof Error ? error.message : t("Form not found")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col items-center">
      <div className="flex flex-col w-full max-w-[650px] flex-1 min-h-0">
        <PendingFormContent
          formResponse={formResponse}
          onComplete={onComplete}
          surface="form-link"
          automationStepId={automationStepId}
          documentGroupBackUrl={documentGroupBackUrl}
          publicIdentifier={publicIdentifier}
          restartUrl={window.location.href}
        />
      </div>
    </div>
  );
};
